import { Controller } from "@hotwired/stimulus"

// Disables a submit button for a configurable duration to prevent spam.
// Persists expiry in localStorage so the cooldown survives page reloads.
//
// Usage:
//   <div data-controller="cooldown"
//        data-cooldown-seconds-value="90"
//        data-cooldown-key-value="resend-pw-123"
//        data-action="turbo:submit-start->cooldown#start">
//     <%= button_to ..., data: { "cooldown-target": "button" } do %>
//       <span data-cooldown-target="label">Send</span>
//     <% end %>
//   </div>
export default class extends Controller {
  static targets = ["button", "label"]
  static values = {
    seconds: { type: Number, default: 90 },
    key: String
  }

  connect() {
    this.originalLabel = this.hasLabelTarget ? this.labelTarget.textContent.trim() : ""
    this.boundTick = this.tick.bind(this)
    this.refresh()
  }

  disconnect() {
    if (this.interval) clearInterval(this.interval)
  }

  start() {
    const expiresAt = Date.now() + this.secondsValue * 1000
    localStorage.setItem(this.storageKey, String(expiresAt))
    this.refresh()
  }

  refresh() {
    const expiresAt = parseInt(localStorage.getItem(this.storageKey) || "0", 10)
    if (!expiresAt || expiresAt <= Date.now()) {
      this.enable()
      return
    }
    this.disable()
    if (this.interval) clearInterval(this.interval)
    this.interval = setInterval(this.boundTick, 1000)
    this.tick()
  }

  tick() {
    const expiresAt = parseInt(localStorage.getItem(this.storageKey) || "0", 10)
    const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000))
    if (remaining <= 0) {
      this.enable()
      clearInterval(this.interval)
      return
    }
    if (!this.hasLabelTarget) return
    const m = Math.floor(remaining / 60)
    const s = remaining % 60
    this.labelTarget.textContent = `Espera ${m}:${s.toString().padStart(2, "0")}`
  }

  enable() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = false
      this.buttonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
    if (this.hasLabelTarget && this.originalLabel) {
      this.labelTarget.textContent = this.originalLabel
    }
  }

  disable() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
  }

  get storageKey() {
    return `cooldown:${this.keyValue}`
  }
}
